<template>
  <div :class="cssClasses">
    <div class="eventInformation">
      <v-container class="pt-0">
        <v-layout row wrap>
          <v-flex sm7 class="information">
            <h4 v-if="extract('event.open')">Register for</h4>
            <h3
              v-html="
                extract('event.content.body.title') || extract('event.name')
              "
            ></h3>
            <div
              class="event-description"
              v-html="extract('event.content.body.description')"
            ></div>
            <table
              class="event-sections"
              v-if="extract('event.content.body.sections')"
            >
              <tr
                v-for="(obj, num) in extract('event.content.body.sections')"
                v-bind:key="num"
              >
                <td class="rowLabel">
                  <h4 class="section-title">
                    {{
                      extract("event.content.body.sections." + num + ".title")
                    }}:
                  </h4>
                </td>
                <td
                  v-html="
                    extract('event.content.body.sections.' + num + '.html')
                  "
                ></td>
              </tr>
            </table>

            <div
              class="event-closing"
              v-if="extract('event.content.body.closing')"
              v-html="extract('event.content.body.closing')"
            ></div>
            <div>
              <span class="capacity-notice" v-if="!loading && availableSlots < 1"
                >The event is currently at capacity, but sign up below to be
                added to the wait list and we'll email you if a spot opens
                up.</span
              >
            </div>
          </v-flex>
          <v-flex sm4 offset-sm1 class="callout">
            <!-- IF DEFAULT ASIDE -->
            <div class="whiteBox pa-4 mb-4 hidden-xs-only">
              <div>
                <div>
                  <p>
                    <img
                      style="max-width: 100%"
                      class="w-100"
                      src="https://amaze-assets.s3.us-west-1.amazonaws.com/public/rethink-education.jpg"
                    />
                  </p>
                </div>
              </div>
            </div>
            <!-- END DEFAULT ASIDE -->
            <div id="refund-dialog">
              <v-dialog v-model="dialog" max-width="500px">
                <v-btn
                  slot="activator"
                  color="primary"
                  style="display: none;"
                  id="refund-dialog-button"
                  class="mb-4 mt-4 ml-0"
                >
                  Learn more about RTI's refund and cancellation policy
                </v-btn>
                <v-card>
                  <v-card-title>
                    <h3 class="headline">Refund and Cancellation Policy</h3>
                  </v-card-title>
                  <hr class="thick" />

                  <v-card-text>
                    <v-layout wrap>
                      <div class="pa-2 mb-2">
                        <p>
                          The {{ event.name }} Cancellation and Refund Policy
                          will be as follows to assure consistency and is
                          applicable to this event.
                        </p>
                        <p>
                          {{ event.name }} registration canceled online on or
                          before {{ getFormattedDate(event.refund_expiry) }} is
                          refundable if already paid, or not due if not already
                          paid. In order for your cancellation to be received,
                          email
                          <a href="mailto:educationservices@rti.org ">
                            educationservices@rti.org</a
                          >
                          no later than
                          {{ getFormattedDate(event.refund_expiry) }}.
                        </p>
                      </div>
                    </v-layout>
                  </v-card-text>

                  <v-card-actions>
                    <v-spacer />
                    <v-btn color="blue darken-1" flat @click="dialog = false"
                      >Cancel</v-btn
                    >
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </div>
          </v-flex>
        </v-layout>
      </v-container>
    </div>

    <!-- BEGIN THE FORMATIONING -->
    <div class="registrationForm" v-if="!loading">
      <div class="text-xs-center" v-if="!extract('event.open')">
        <h4>Registration is closed for this event.</h4>
      </div>
      <v-form ref="regForm" v-model="valid" v-if="extract('event.open')">
        <v-container>
          <v-layout row wrap>
            <v-flex md6>
              <h3>
                Registration
                <small>Required fields <span class="star">*</span></small>
              </h3>

              <h5 v-if="!registrationIsSingle">
                School &amp; Team Information
              </h5>

              <v-text-field v-model="schoolName" required :rules="rules.school">
                <template slot="label"
                  >School Name<span class="star">*</span></template
                >
              </v-text-field>

              <v-text-field
                v-model="districtName"
                required
                :rules="rules.district"
              >
                <template slot="label"
                  >District Name<span class="star">*</span></template
                >
              </v-text-field>

              <v-text-field v-model="stateName" required :rules="rules.state">
                <template slot="label"
                  >State<span class="star">*</span></template
                >
              </v-text-field>

              <div id="affiliationsContainer md6">
                <h5 class="pb-2 mb-0">
                  Rethink Education Affiliation.<span class="star">*</span>
                </h5>
                <p class="">
                  <strong
                    >You do not have to be affiliated with Rethink Education to
                    participate.</strong
                  >
                  This information helps us to understand who is participating
                  in the networking events. <br />Mark all that apply.
                </p>
                <v-checkbox
                  class="py-1 my-0"
                  v-model="affiliation"
                  v-for="n in affiliations"
                  :key="n"
                  :label="n"
                  :value="n"
                  required
                  :rules="rules.affiliation"
                ></v-checkbox>
              </div>

              <div v-if="maxRegistrants > 1">
                <h5 class="pb-2 mb-0">
                  Size of school team (total registrants)<span class="star"
                    >*</span
                  >
                </h5>
                <p class="mt-0 pt-0">
                  After selecting the size of your team, you will be able to
                  enter attendee information
                </p>
                <v-select
                  :items="registrantSlots"
                  label="Select Size"
                  v-model="registrants"
                  required
                  :rules="rules.size"
                  @change="initializeSlots"
                ></v-select>

                <div v-if="registrants > 0">
                  <h5>Attendee Information</h5>
                  <p>
                    Please enter the registration information (name, email and
                    role) for each person on your school team.
                    <strong>Registrant 1</strong> will receive email
                    confirmation and payment information.
                  </p>

                  <template v-for="(obj, num) in registrant">
                    <div class="registrant" :key="num">
                      <h6>Registrant {{ num + 1 }}</h6>
                      <v-text-field
                        v-model="registrant[num].name"
                        required
                        :rules="rules.name"
                      >
                        <template slot="label">
                          Registrant {{ num + 1 }}: Name<span class="star"
                            >*</span
                          >
                        </template>
                      </v-text-field>
                      <AmazeEmailAddressInput v-model="registrant[num].email">
                        <template slot="label"
                          >Registrant {{ num + 1 }}: E-mail</template
                        >
                      </AmazeEmailAddressInput>

                      <v-select
                        :items="roles"
                        v-model="registrant[num].role"
                        required
                        :rules="rules.role"
                      >
                        <template slot="label">
                          Registrant {{ num + 1 }}: Role<span class="star"
                            >*</span
                          >
                        </template>
                      </v-select>
                    </div>
                  </template>
                </div>
              </div>
              <div v-if="maxRegistrants == 1">
                <v-text-field
                  v-model="registrant[0].name"
                  required
                  :rules="rules.name"
                >
                  <template slot="label">
                    Name<span class="star">*</span>
                  </template>
                </v-text-field>
                <AmazeEmailAddressInput v-model="registrant[0].email">
                  <template slot="label">E-mail</template>
                </AmazeEmailAddressInput>
                <v-select
                  :items="roles"
                  v-model="registrant[0].role"
                  required
                  :rules="rules.role"
                >
                  <template slot="label">
                    Role<span class="star">*</span>
                  </template>
                </v-select>
              </div>
            </v-flex>
            <v-flex xs12 sm12 md12 lg12 class="mt-20">
              <div class="submit-wrapper" v-if="registrants > 0">
                <div class="submit-success" v-if="this.submittedSuccessfully">
                  <h5>Success!</h5>
                  <p>
                    <strong
                      >Thank you for registering for the
                      <span
                        v-html="
                          extract('event.content.body.title') ||
                            extract('event.name')
                        "
                      ></span
                      >!</strong
                    >
                    You should receive a confirmation email shortly. If you do
                    not receive an email, please contact
                    <a :href="'mailto:' + contactAddress">{{
                      contactAddress
                    }}</a
                    >.
                  </p>
                </div>
                <div class="submit-incomplete" v-else>
                  <div v-if="terms || acknowledgement">
                    <h5 v-if="terms || acknowledgement">
                      Terms and Conditions
                    </h5>
                    <div v-if="terms || acknowledgement">
                      <div id="netTermsWrapper">
                        <AmazeTermsViewer :src="terms"></AmazeTermsViewer>
                      </div>
                      <div
                        v-if="acknowledgement"
                        v-html="acknowledgement"
                        id="acknowledgement"
                      ></div>
                      <div v-if="acknowledgement">
                        <v-checkbox
                          required
                          :rules="rules.acknowledge"
                          v-model="acknowledge"
                          :disabled="acknowledge"
                          :label="
                            `I acknowledge that I have read and accept the terms and conditions.`
                          "
                        ></v-checkbox>
                      </div>
                    </div>
                  </div>
                  <div v-else>
                    <h5>Complete Registration</h5>
                    <div v-if="!registrationIsSingle">
                      <p>
                        After submitting this form,
                        <strong>Registrant 1</strong> will receive an email
                        confirming your school’s registration.
                      </p>
                      <p>
                        <strong>Please note:</strong> all team members will
                        receive an email with a personalized link to review and
                        accept terms and conditions. The terms and conditions
                        must be accepted prior to the event.
                      </p>
                    </div>
                    <div v-if="registrationIsSingle">
                      <p v-if="showWaiver">
                        After submitting this form, you will be prompted to
                        review our terms and conditions to finalize your
                        registration. You will also receive a confirmation email
                        with your registration details.
                      </p>
                      <p v-else>
                        After submitting this form, you will receive a
                        confirmation email with your registration details.
                      </p>
                    </div>
                  </div>
                  <v-btn
                    v-if="!submitted"
                    class="ml-0"
                    color="primary"
                    @click="submitRegistration()"
                    >Submit Registration</v-btn
                  >
                  <v-progress-circular v-else indeterminate />
                </div>
              </div>
            </v-flex>
          </v-layout>
        </v-container>
      </v-form>
    </div>

    <!-- END THE FORMATIONING -->
  </div>
</template>
<style scoped lang="scss">
/*
 * The affiliations checkboxes contain options with long labels.
 * This helps minimize line breaks in those options until it is necessary
 * due to window width contraints.
 */
#affiliationsContainer {
  margin-right: -200%;
}
@media (max-width: 669px) {
  #affiliationsContainer {
    margin-right: 0;
    max-width: 395px;
  }
}

/** deep **/
::v-deep
  .v-input--selection-controls:not(.v-input--hide-details)
  .v-input__slot {
  margin-bottom: 4px;
}
::v-deep small {
  font-size: 55%;
}

::v-deep .information {
  /* Gotta do this because we need that subtitle, but it's small. */
  .small {
    font-size: 60%;
    display: block;
  }
}
::v-deep .submit-wrapper {
  /* Gotta do this because we need that subtitle, but it's small. */
  .small {
    &::before {
      content: " for ";
    }
  }
}
</style>
<script>
import registrationMixin from "@/mixins/registrationMixin.js";

export default {
  mixins: [registrationMixin],
  data() {
    return {
      dialog: false,
      loading: true,
      valid: false,
      submitted: false,
      acknowledge: false,

      availableSlots: 0,
      maxRegistrants: 0,
      totalAttendees: 0,

      schoolName: "",
      districtName: "",
      stateName: "",

      roles: ["Principal", "Teacher", "Counselor", "College Liaison", "Other"],

      affiliations: [
        "Facilitator Cohort (Professional Development)",
        "Partnership with RTI for Technical Assistance",
        "Accessing K-8 Blended Learning Content",
        "No Affiliation"
      ],
      affiliation: [],

      registrants: 0,
      numRegistrants: 0,
      registrant: [],
      submittedSuccessfully: false,
      rules: {
        name: [v => !!v || "Name is required."],
        email: [
          v => !!v || "Email is required.",
          v => /@/.test(v) || "Please enter a valid email."
        ],
        role: [v => !!v || "Role is required."],
        school: [v => !!v || "School name is required."],
        district: [v => !!v || "District name is required."],
        state: [v => !!v || "State is required."],
        size: [v => !!v || "Team size is required."],
        affiliation: [v => (!!v && v.length > 0) || "Affiliation is required."],
        acknowledge: [v => !!v || "Please accept the terms and conditions."]
      },
      event: {}
    };
  },
  computed: {
    classes() {
      return "";
    },
    registrationIsSingle() {
      return this.maxRegistrants === 1;
    },
    registrantSlots() {
      let slots = [];
      for (let i = 1; i <= this.maxRegistrants; i++) {
        if (i === 1) {
          slots.push({ text: i + " attendee", value: i });
        } else {
          slots.push({ text: i + " attendees", value: i });
        }
      }
      return slots;
    },
    loaded() {
      return !this.loading;
    },
    showSubmitPrompt() {
      return !this.submittedSuccessfully && this.registrants > 0;
    }
  },
  methods: {
    initializeSlots() {
      let backup = this.registrant;
      this.registrant = [];
      for (let person = 0; person < this.registrants; person++) {
        if (backup[person]) {
          this.registrant[person] = backup[person];
        } else {
          this.registrant[person] = {
            name: "",
            email: null,
            role: null
          };
        }
      }
    },
    submitRegistration() {
      if (this.$refs.regForm.validate()) {
        this.submitted = true;
        if (this.terms) {
          this.registrant[0].terms = this.acknowledge;
        }
        let data = {
          school: this.schoolName,
          district: this.districtName,
          state: this.stateName,
          team_total: this.registrants,
          registration_event_id: this.extract("event.id"),
          registration_team_member: this.registrant,
          single_registration: this.registrationIsSingle,
          extra_attributes: {
            // Add affiliation.
            affliations: this.affiliation,
            // Add date selected.
            date: this.extract("event.dates"),
            // Specify audience.
            session: this.extract("event.content.body.title")
              .replace('<span class="small">', " for ")
              .replace("</span>", "")
          }
        };

        if (this.registrationIsSingle) {
        }

        this.$http.post("registrations", data).then(
          response => {
            if (this.registrationIsSingle && this.showWaiver) {
              this.$router.push({
                path:
                  "/event/terms/" +
                  response.body.data.registration_team_member[0].uuid
              });
              this.$store.commit("updateMessage", {
                message:
                  "Please review the terms and conditions to complete registration.",
                snackbar: true
              });
            } else {
              this.submittedSuccessfully = true;
            }
          },
          response => {
            this.submitted = false;
            this.$store.commit("updateMessage", {
              message:
                "There was a problem saving your registration. Please try again or contact support.",
              snackbar: true
            });
          }
        );
      } else {
        this.$store.commit("updateMessage", {
          message: "Required fields are missing. Please review the form.",
          snackbar: true
        });
      }
    }
  },
  mounted() {
    this.$http
      .get(
        "event/" +
          (this.$route.params.slug || this.$route.path.split("/").pop())
      )
      .then(
        response => {
          this.event = response.data.data[0];

          this.totalAttendees = this.extract("event.total_attendees") || 0;
          this.maxAttendees = this.extract("event.max_attendees") || 0;

          this.availableSlots = Math.max(
            0,
            this.maxAttendees - this.totalAttendees
          );

          this.maxRegistrants = this.extract("event.max_per_team");
          // If we need to limit registration to only accept the max number of slots, we can use the following logic:
          // Math.min(this.extract('event.max_per_team'), (this.maxAttendees - this.totalAttendees));

          var roles = this.extract("event.content.form.roles");

          if (roles && roles.length > 0) {
            this.roles = roles;
          }

          var fields = this.extract("event.content.form.fields");

          if (fields) {
            for (const key in fields) {
              var field = fields[key];
              // Set defaults.
              if (field.hasOwnProperty("default")) {
                this[key] = field.default;
              }
              // Set values.
              if (field.hasOwnProperty("values")) {
                this[key] = field.values;
              }
            }
          }

          if (this.maxRegistrants === 1) {
            // Single registration, set-up initial object to avoid null object errors.
            this.registrant.push({ name: "", email: null, role: null });
            this.registrants = 1;
          }

          this.loading = false;
          // eslint-disable-next-line handle-callback-err
        },
        error => {
          this.$router.push("/404");
        }
      );
  }
};
</script>
